import React, { Fragment } from 'react';
import {
  Body, Column,
  Container,
  Head,
  Heading,
  Hr,
  Html,
  Img,
  Link,
  Preview,
  Row,
  Section,
  Tailwind,
  Text
} from '@react-email/components';
import {
  getEffectiveSeverity,
  getFieldWithDefault,
  Opportunity,
  opportunityCategorySeverityEmoji,
  opportunityCategorySeverityTextClassSeverityMessage
} from '@/opportunity/opportunity';
import { format, getYear, parseISO } from "date-fns";

const OpportunityDescriptionEmail = (
  {
    opportunity,
    shareableLink,
  }: {
    opportunity: Opportunity;
    shareableLink: string;
  }
) => {
  const baseUrl = window.location.origin;

  const defaultDateFormat = "PPP"
  const today = new Date();

  const generalSupportEmail = "copilot@thalolabs.com"
  const customerSupportEmail = "doneliza@thalolabs.com";
  const customerSupportTel = "646-883-6122"

  return (
    <Html>
      <Tailwind>
        <Head/>
        <Fragment>
          <Preview>Opportunity Update: {opportunity.name}</Preview>
          <Body style={main}>
            <Section className="p-4 bg-[#000F89] dark:invert-0 text-white">
              <Container>
                <Row>
                  <Heading className="my-0">
                    Copilot
                  </Heading>
                </Row>
                <Row>
                  <Column className="text-lg w-0 pr-2">
                    by
                  </Column>
                  <Column>
                    <Img
                      src={`${baseUrl}/thalo-light.png`}
                      height="30px"
                      alt="Thalo Labs Logo"
                    />
                  </Column>
                </Row>
              </Container>
            </Section>
            <Container>
              <Section>
                <Heading as="h2">
                  Issue detected
                </Heading>
                <Text>
                  <strong>Reporting
                    period:</strong> {format(parseISO(opportunity.identified_date), defaultDateFormat)} - {format(today, defaultDateFormat)}
                </Text>
                <Container className="bg-gray-50 p-2">
                  <Text className="mt-1 mb-2">
                    <strong>{opportunity.name} at {opportunity.location.name}</strong>
                  </Text>
                  {
                    getEffectiveSeverity(opportunity) && (
                      <Text className="my-0">
                        {opportunityCategorySeverityEmoji[getEffectiveSeverity(opportunity)!]} {opportunityCategorySeverityTextClassSeverityMessage[getEffectiveSeverity(opportunity)!]}
                      </Text>
                    )
                  }
                </Container>
                <Text>
                  <strong>Summary:</strong> {getFieldWithDefault(opportunity, "issue_summary", "default_issue_summary")}
                </Text>
                {
                  opportunity.reviewer_notes_customer_facing && (
                    <Text>
                      <strong>Reviewer notes:</strong> {opportunity.reviewer_notes_customer_facing}
                    </Text>
                  )
                }
              </Section>
              <Hr/>
              <Section>
                <Heading as="h2">
                  Issue details: {opportunity.name}
                </Heading>
                <Section style={buttonSection}>
                  <Link
                    style={buttonLink}
                    href={shareableLink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View more details for this issue on Thalo Copilot
                  </Link>
                </Section>
                <Container className="bg-gray-50 p-4 mb-4">
                  <Text className="mt-0">
                    <strong>Issue type:</strong> {opportunity.category}
                  </Text>
                  <Text className="mb-0">
                    <strong>Detections:</strong> {opportunity.number_of_detections} times since {format(parseISO(opportunity.identified_date), defaultDateFormat)}
                  </Text>
                </Container>
                <Container className="bg-gray-50 p-4 mb-4">
                  <Text className="mt-0">
                    <strong>What's happening?</strong>
                  </Text>
                  <Text
                    className="mb-0"
                    dangerouslySetInnerHTML={{ __html: getFieldWithDefault(opportunity, "what_is_happening", "default_what_is_happening")}}
                  />
                </Container>
                {
                  !!opportunity.highest_severity_instance?.plots.length && (
                    <Container className="bg-gray-50 p-4 mb-4">
                      <Heading className="mt-0" as="h3">
                        Plots
                      </Heading>
                      {
                        opportunity.highest_severity_instance.plots.map((plot, index) => (
                          <div key={index} style={plotStyle}>
                            <Heading as="h4">
                              {plot.title}
                            </Heading>
                            <Text>{plot.description}</Text>
                            {plot.filepath && (
                              <Img src={plot.filepath} alt={plot.title} style={plotImage}/>
                            )}
                          </div>
                        ))
                      }
                    </Container>
                  )
                }
              </Section>
              <Section style={buttonSection}>
                <Link
                  style={buttonLink}
                  href={shareableLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View more details for this issue, including potential causes and actions, on Thalo Copilot
                </Link>
              </Section>
              <Hr/>
              <Section>
                <Heading as="h2">
                  Support
                </Heading>
                <Text>
                  Respond to this email, or contact <Link href={`mailto:${generalSupportEmail}`}>{generalSupportEmail}</Link> with any questions.
                </Text>
                <Text>
                  Your customer support contact is <Link href={`mailto:${customerSupportEmail}`}>Doneliza Joaquin</Link> (<Link href={`tel:${customerSupportTel}`}>{customerSupportTel}</Link>).
                </Text>
              </Section>
            </Container>
            <Section className="bg-[#000F89] p-4">
              <Container className="text-white">
                © Thalo Labs {getYear(today)}
              </Container>
            </Section>
          </Body>
        </Fragment>
      </Tailwind>
    </Html>
  );
};

export default OpportunityDescriptionEmail;

const main = {
  fontFamily: '"Google Sans",Roboto,RobotoDraft,Helvetica,Arial,sans-serif',
  margin: "0",
};

const attachmentStyle = {
  marginBottom: "20px",
};

const attachmentTitle = {
  fontWeight: "bold",
  fontSize: "16px",
};

const attachmentImage = {
  width: "100%",
  borderRadius: "8px",
};

const plotStyle = {
  marginBottom: "20px",
};

const plotTitle = {
  fontWeight: "bold",
  fontSize: "16px",
};

const plotImage = {
  width: "100%",
  borderRadius: "8px",
};

const buttonSection = {
  textAlign: "center" as const,
  marginTop: "-20px",
  marginBottom: "20px",
  background: "#fff",
  padding: "10px 24px",
};

const buttonLink = {
  display: "inline-block",
  padding: "10px 20px",
  backgroundColor: "#15c",
  color: "#fff",
  textDecoration: "none",
  borderRadius: "5px",
};